import { useEffect, useRef, type ReactNode } from 'react';
import { Plus, Settings } from 'lucide-react';
import { NavLink, useLocation } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { BrandMark } from '../ui/BrandMark';
import { listRooms } from '../db/rooms';
import { iconByName } from './roomIcons';
import { iconOrdinals } from './iconOrdinals';
import { useRoomDialogs } from './RoomDialogs';
import './Rail.css';

/**
 * 72px, --sand, no border. It separates from the content by being the same
 * colour as the page background while the content sits on --paper.
 *
 * The rail never scrolls; the room list in the middle does.
 *
 * No hover tooltips: the dark bubble read as a smudge against the rail. The
 * `aria-label` on every control is what actually makes these accessible, and
 * it stays.
 */
export function Rail() {
  const rooms = useLiveQuery(listRooms, []) ?? [];
  const dialogs = useRoomDialogs();
  const ordinals = iconOrdinals(rooms);
  const scroller = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  /**
   * Keep the open room where you can see it.
   *
   * On a phone the rail is a bottom bar and the rooms scroll sideways, so
   * past the sixth or seventh the selected one can sit off the end — the bar
   * then shows a row of rooms with nothing marked, which reads as nothing
   * being open at all. Arriving by search, by a link, or by reload all land
   * there, not just tapping.
   *
   * Scrolls the container rather than calling `scrollIntoView`, which is free
   * to scroll every ancestor it fancies — including the page behind a fixed
   * bar. Horizontal only, so on the desktop rail, where the overflow is
   * vertical, every one of these is a no-op.
   */
  useEffect(() => {
    const box = scroller.current;
    const active = box?.querySelector<HTMLElement>('.rail__button--active');
    if (box === null || active === null || active === undefined) return;

    const edge = 16;
    const view = box.getBoundingClientRect();
    const item = active.getBoundingClientRect();

    // Instant, not smooth. A scroll only happens when the room is off the end
    // — when it is already visible this does nothing at all — and in that case
    // there is no motion to follow with your eye anyway: the bar simply has
    // the right thing in it by the time you look. Instant is also the version
    // that cannot half-happen, which an interrupted animation can.
    if (item.left < view.left + edge) {
      box.scrollBy({ left: item.left - view.left - edge, behavior: 'auto' });
    } else if (item.right > view.right - edge) {
      box.scrollBy({ left: item.right - view.right + edge, behavior: 'auto' });
    }
    // `rooms.length` so this also runs on the first paint after the live
    // query resolves, when the buttons it looks for finally exist.
  }, [pathname, rooms.length]);

  return (
    <nav className="rail" aria-label="Rooms">
      <div className="rail__group">
        {/* The logo carries far more detail than a lucide glyph, so its
            button is oversized and it keeps its own colours. */}
        <RailLink to="/" label="Home" end className="rail__button--brand">
          <BrandMark size={40} />
        </RailLink>

        <button
          type="button"
          className="rail__button"
          aria-label="Add Room"
          onClick={dialogs.openCreate}
        >
          <Plus size={20} aria-hidden />
        </button>
      </div>

      <hr className="rail__divider" />

      <div className="rail__rooms" ref={scroller}>
        {rooms.map((room) => {
          const Icon = iconByName(room.icon);
          const ordinal = ordinals.get(room.id);
          return (
            // No `end`: the room stays active while a section or an item
            // panel is open further down the same route.
            <RailLink key={room.id} to={`/room/${room.id}`} label={room.name}>
              <Icon size={20} aria-hidden />
              {/* Icons only down here, so two rooms sharing one are otherwise
                  the same button twice. */}
              {ordinal !== undefined && <span className="rail__ordinal">{ordinal}</span>}
            </RailLink>
          );
        })}
      </div>

      <div className="rail__group rail__group--bottom">
        <RailLink to="/settings" label="Settings">
          <Settings size={20} aria-hidden />
        </RailLink>
      </div>
    </nav>
  );
}

function RailLink({
  to,
  label,
  end = false,
  className = '',
  children,
}: {
  to: string;
  label: string;
  end?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      aria-label={label}
      className={({ isActive }) =>
        `rail__button ${className}${isActive ? ' rail__button--active' : ''}`
      }
    >
      {children}
    </NavLink>
  );
}
